import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createAppTheme } from '../theme/createAppTheme';
import {
  DEFAULT_THEME_PREFERENCES,
  loadThemePreferences,
  saveThemePreferences,
} from '../theme/themeStorage';

const ThemePreferenceContext = createContext(null);

export const ThemePreferenceProvider = ({ children }) => {
  const [preferences, setPreferencesState] = useState(() => loadThemePreferences());

  const setPreferences = useCallback((patch) => {
    setPreferencesState((prev) => {
      const next = { ...prev, ...patch };
      saveThemePreferences(next);
      return next;
    });
  }, []);

  const resetPreferences = useCallback(() => {
    setPreferencesState({ ...DEFAULT_THEME_PREFERENCES });
    saveThemePreferences(DEFAULT_THEME_PREFERENCES);
  }, []);

  const theme = useMemo(
    () =>
      createAppTheme({
        mode: preferences.mode,
        paletteId: preferences.paletteId,
        borderRadius: preferences.borderRadius,
      }),
    [preferences.mode, preferences.paletteId, preferences.borderRadius]
  );

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme-mode', preferences.mode);
    root.setAttribute('data-theme-palette', preferences.paletteId);
    root.style.setProperty('--brand-primary', theme.palette.primary.main);
    root.style.setProperty('--brand-accent', theme.brand?.accent ?? '#FF8C42');
    root.style.setProperty('--brand-gradient', theme.brand?.gradient ?? '');
    root.style.setProperty('--app-radius', `${theme.shape.borderRadius}px`);
    root.style.setProperty('--text-primary', theme.palette.text.primary);
    root.style.setProperty('--text-secondary', theme.palette.text.secondary);
    root.style.setProperty('--bg-default', theme.palette.background.default);
    root.style.setProperty('--bg-paper', theme.palette.background.paper);
  }, [preferences.mode, preferences.paletteId, theme]);

  const value = useMemo(
    () => ({
      preferences,
      theme,
      setMode: (mode) => setPreferences({ mode }),
      setPaletteId: (paletteId) => setPreferences({ paletteId }),
      setBorderRadius: (borderRadius) => setPreferences({ borderRadius }),
      toggleMode: () =>
        setPreferences({ mode: preferences.mode === 'light' ? 'dark' : 'light' }),
      resetPreferences,
      setPreferences,
    }),
    [preferences, theme, setPreferences, resetPreferences]
  );

  return (
    <ThemePreferenceContext.Provider value={value}>{children}</ThemePreferenceContext.Provider>
  );
};

export const useThemePreference = () => {
  const ctx = useContext(ThemePreferenceContext);
  if (!ctx) {
    throw new Error('useThemePreference must be used within ThemePreferenceProvider');
  }
  return ctx;
};
